/**
 * Group Session Types
 *
 * Types for the Group Prioritization feature — a real-time, multi-member
 * weighted-scoring flow that runs inside a prioritization_sessions row.
 *
 * The session row carries two new columns:
 *   - group_session_status ('SETUP'|'OPEN'|'SCORING'|'REVEALING'|'DECIDED')
 *   - group_join_token (sharable join link token)
 *
 * Supporting tables:
 *   - group_criteria         — weighted scoring dimensions defined by the facilitator
 *   - group_scores           — per-member, per-item, per-criterion scores (1–5)
 *   - group_rank_adjustments — deliberation changes made during S6 vs Pre-S6 baseline
 *
 * Three distinct artifacts are produced through the prioritization flow:
 *   1. Pre-S6 Results   — System Recommendation (algorithmic output of S1-S5)
 *   2. Post-S6 Results  — Group Recommendation (collective judgment applied in S6)
 *   3. Final Ranking    — Official Decision (leadership-approved commitment)
 *
 * These artifacts must never overwrite one another. Each is a permanent record
 * of a distinct stage of judgment in the prioritization process.
 */

// ─── Ranking Artifact Kinds ───────────────────────────────────────────────────

/**
 * The three distinct artifacts produced during the prioritization process.
 *
 * - system_recommendation: Output of the S1-S5 prioritization model (Pre-S6 Results)
 * - group_recommendation:  Output of S6 group deliberation (Post-S6 Results)
 * - official_decision:     Leadership-approved final commitment
 *
 * These never overwrite one another. Each is a permanent record of a distinct
 * stage of judgment.
 */
export type RankingArtifactKind =
  | 'system_recommendation'
  | 'group_recommendation'
  | 'official_decision';

export const RANKING_ARTIFACT_KIND_LABELS: Record<RankingArtifactKind, string> = {
  system_recommendation: 'System Recommendation',
  group_recommendation: 'Group Recommendation',
  official_decision: 'Decision of Record',
};

export const RANKING_ARTIFACT_KIND_DESCRIPTIONS: Record<RankingArtifactKind, string> = {
  system_recommendation:
    'The ranking produced by the prioritization model based on configured criteria, weights, and scores.',
  group_recommendation:
    'The ranking produced by collective judgment after the group reviewed and deliberated on the system recommendation.',
  official_decision:
    'The ranking approved by leadership and committed to execution.',
};

// ─── Pre-S6 Results (System Recommendation) ───────────────────────────────────

/**
 * A single item's position in the Pre-S6 (system-generated) ranking.
 */
export interface PreS6RankedItem {
  itemId: string;
  title: string;
  /** 1-based rank from the prioritization model. */
  rank: number;
  /** Weighted score produced by the model. */
  score: number;
  /** Normalized score (0–100). */
  normalizedScore?: number;
  /** Tier assignment if applicable. */
  tier?: string;
}

/**
 * A snapshot of the system recommendation taken at the moment S6 begins.
 *
 * This is captured once and never mutated. It serves as the reference baseline
 * for the S6 group deliberation and for the post-session audit trail.
 */
export interface PreS6RankingSnapshot {
  /** ID of the S5 session whose output this snapshot was taken from. */
  sourceSessionId: string;
  /** Items in rank order (rank 1 = highest priority). */
  rankedItems: PreS6RankedItem[];
  /** When the snapshot was taken (at S6 session open). */
  capturedAt: string;
  /** Which tool produced the underlying ranking. */
  toolId?: string;
  toolName?: string;
}

// ─── Group Rank Adjustments ───────────────────────────────────────────────────

/**
 * A deliberate change made during S6 to an item's position relative to the
 * system recommendation.
 *
 * Captures the audit trail of where the group's judgment departed from the
 * model and why.
 */
export interface GroupRankAdjustment {
  id: string;
  itemId: string;
  itemTitle: string;
  /** Rank in the Pre-S6 system recommendation. */
  originalRank: number;
  /** Rank in the Post-S6 group recommendation. */
  revisedRank: number;
  /** Direction of movement relative to the model. */
  direction: 'elevated' | 'lowered' | 'unchanged';
  /** Mandatory rationale when the group departs from the model. */
  rationale: string;
  /** Contextual factors discussed (e.g. dependencies, timing, strategic). */
  contextualFactors?: string[];
  recordedBy: string;
  recordedAt: string;
  /** IDs of participants who participated in this deliberation. */
  participantIds: string[];
}

// ─── Status ──────────────────────────────────────────────────────────────────

/** The five lifecycle states of a Group Prioritization session. */
export type GroupSessionStatus =
  | 'SETUP'      // Facilitator is configuring criteria and adding options
  | 'OPEN'       // Waiting for members to join; scoring not yet started
  | 'SCORING'    // Members are actively scoring options
  | 'REVEALING'  // Facilitator has triggered result reveal (read-only view)
  | 'DECIDED';   // A winner has been selected; session is closed

// ─── Database-mapped entities ─────────────────────────────────────────────────

/**
 * GroupCriterion — maps to a row in the group_criteria table.
 * Each criterion has a weight that is applied when computing weighted scores.
 */
export interface GroupCriterion {
  id: string;
  workspaceId: string;
  sessionId: string;
  title: string;
  description?: string;
  /** Relative importance weight; weights are summed and normalised during computation. */
  weight: number;
  /** Display order (0-based). */
  position: number;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * GroupScore — maps to a row in the group_scores table.
 * One row per (session, item, criterion, user) tuple.
 */
export interface GroupScore {
  id: string;
  workspaceId: string;
  sessionId: string;
  itemId: string;
  criteriaId: string;
  userId: string;
  /** Integer 1–5. */
  score: number;
  note?: string;
  createdAt: Date;
  updatedAt: Date;
}

// ─── View models ─────────────────────────────────────────────────────────────

/**
 * GroupOption — a lightweight view of an item as it appears in a group session.
 * Populated from the items table; only the fields needed for scoring are included.
 */
export interface GroupOption {
  id: string;
  workspaceId: string;
  title: string;
  description?: string;
  url?: string;
}

/**
 * GroupSession — the session row projected through the group-session lens.
 * Includes the new group_session_status and group_join_token columns.
 */
export interface GroupSession {
  id: string;
  workspaceId: string;
  title: string;
  description?: string;
  groupSessionStatus: GroupSessionStatus;
  /** Token distributed to members for joining via link. */
  joinToken?: string;
  /**
   * Standard-deviation threshold above which a criterion result is flagged as
   * divergent (members disagree significantly).
   */
  divergenceThreshold: number;
  facilitatorId: string;
  /** Ordered list of item IDs in this session's scoring pool. */
  itemIds: string[];
  createdAt: Date;
  updatedAt: Date;

  /**
   * Pre-S6 Results: snapshot of the system recommendation taken when S6 opened.
   *
   * Captured once from the S1-S5 model output and never mutated.
   * Serves as the baseline for group deliberation and post-session comparison.
   * Corresponds to `pre_s6_ranking_snapshot` on the DB row.
   */
  preS6Snapshot?: PreS6RankingSnapshot;

  /**
   * Deliberation adjustments recorded during S6: where and why the group
   * departed from the system recommendation.
   *
   * Populated progressively as the host records rationale for position changes.
   * Corresponds to rows in `group_rank_adjustments`.
   */
  groupRankAdjustments?: GroupRankAdjustment[];
}

// ─── Realtime presence ────────────────────────────────────────────────────────

/**
 * GroupMemberPresence — the payload each member broadcasts on the presence channel.
 * Stored in the Supabase Realtime presence state for the session.
 */
export interface GroupMemberPresence {
  userId: string;
  displayName: string;
  avatarUrl?: string;
  /** Zero-based index of the option the member is currently viewing. */
  currentOptionIndex: number;
  /** Number of options the member has submitted scores for. */
  optionsScored: number;
  /** Total number of options in the session. */
  totalOptions: number;
  status: 'scoring' | 'complete' | 'idle';
}

// ─── Session events ───────────────────────────────────────────────────────────

/**
 * GroupSessionEventType — event_type values written to session_events for group sessions.
 * Additive to the existing event_type vocabulary.
 */
export type GroupSessionEventType =
  | 'member_joined'
  | 'member_progress_updated'
  | 'scoring_opened'
  | 'results_revealing'
  | 'winner_decided';

// ─── Results ─────────────────────────────────────────────────────────────────

/**
 * GroupCriterionResult — aggregated statistics for one criterion on one option.
 */
export interface GroupCriterionResult {
  criteriaId: string;
  criteriaTitle: string;
  /** The weight declared on the criterion row. */
  weight: number;
  /** Arithmetic mean of all member scores for this item+criterion. */
  mean: number;
  /** Population standard deviation of member scores. */
  stdDev: number;
  /** Individual scores, one per member who submitted one. */
  memberScores: { userId: string; displayName: string; score: number }[];
}

/** Per-member participation status for one option. */
export interface GroupMemberParticipation {
  userId: string;
  displayName: string;
  status: 'complete' | 'partial' | 'not_started';
  /** Number of criteria this member has scored for this option. */
  optionsScored: number;
}

/**
 * GroupOptionResult — fully aggregated result for one option, ready for display.
 */
export interface GroupOptionResult {
  itemId: string;
  title: string;
  /** 1 = highest score (winner). */
  rank: number;
  /** Weighted mean score on a 0–5 scale. */
  weightedScore: number;
  /** True when any criterion has stdDev > divergenceThreshold. */
  isDivergent: boolean;
  criteriaBreakdown: GroupCriterionResult[];
  memberParticipation: GroupMemberParticipation[];
}

/**
 * GroupSessionResults — the complete, ranked results payload for a session.
 * Computed client-side for optimistic display via computeGroupResults(), and
 * also returned by the get_group_results RPC.
 *
 * Once the session reaches DECIDED, this represents the Post-S6 Results
 * (Group Recommendation). It is stored immutably and never overwritten by a
 * subsequent Final Ranking (Official Decision).
 */
export interface GroupSessionResults {
  sessionId: string;
  /** Sorted ascending by rank (index 0 = rank 1 = winner). */
  rankedOptions: GroupOptionResult[];
  divergenceThreshold: number;
  totalMembers: number;
  /** ISO 8601 timestamp when the results object was generated. */
  generatedAt: string;

  /**
   * Which artifact this results object represents.
   * During S6, this is 'group_recommendation'. Set at DECIDED time.
   */
  artifactKind?: RankingArtifactKind;

  /**
   * Reference to the Pre-S6 system recommendation that this group result
   * was deliberated against. Used for audit-trail comparison.
   */
  preS6Snapshot?: PreS6RankingSnapshot;

  /**
   * Position deltas relative to the Pre-S6 snapshot.
   * Key = itemId, value = (preS6Rank - postS6Rank): positive = elevated, negative = lowered.
   */
  rankDeltas?: Record<string, number>;
}

// ─── Offline queue ────────────────────────────────────────────────────────────

/**
 * PendingScore — a score submission that could not be sent while offline.
 * Held in the store's pendingScores queue and flushed on reconnect.
 */
export interface PendingScore {
  /** Client-generated temporary ID used for deduplication. */
  tempId: string;
  itemId: string;
  criteriaId: string;
  score: number;
  note?: string;
  queuedAt: Date;
}

// ─── computeGroupResults ──────────────────────────────────────────────────────

/**
 * Compute fully aggregated GroupSessionResults from raw store data.
 *
 * Called client-side to produce an optimistic results view during REVEALING /
 * DECIDED without waiting for the RPC.  The same algorithm should be mirrored
 * in get_group_results on the server.
 *
 * Algorithm:
 *   For each option:
 *     For each criterion:
 *       mean   = arithmetic mean of scores submitted for this (item, criterion)
 *       stdDev = population std-dev of those scores
 *     weightedScore = Σ(criterion.weight × mean) / Σ(criterion.weight)
 *     isDivergent   = any criterion has stdDev > divergenceThreshold
 *   Options are ranked by weightedScore DESC; ties share a rank.
 */
export function computeGroupResults(
  criteria: GroupCriterion[],
  options: GroupOption[],
  scores: GroupScore[],
  divergenceThreshold: number,
  members: Pick<GroupMemberPresence, 'userId' | 'displayName'>[] = [],
): GroupSessionResults {
  const totalMembers = members.length;
  const totalWeight = criteria.reduce((sum, c) => sum + c.weight, 0) || 1;

  const optionResults: Omit<GroupOptionResult, 'rank'>[] = options.map((option) => {
    const criteriaBreakdown: GroupCriterionResult[] = criteria.map((criterion) => {
      const criterionScores = scores.filter(
        (s) => s.itemId === option.id && s.criteriaId === criterion.id,
      );

      const n = criterionScores.length;
      const mean =
        n === 0
          ? 0
          : criterionScores.reduce((sum, s) => sum + s.score, 0) / n;

      const variance =
        n < 2
          ? 0
          : criterionScores.reduce((sum, s) => sum + Math.pow(s.score - mean, 2), 0) / n;
      const stdDev = Math.sqrt(variance);

      const memberScores = criterionScores.map((s) => {
        const member = members.find((m) => m.userId === s.userId);
        return {
          userId: s.userId,
          displayName: member?.displayName ?? s.userId,
          score: s.score,
        };
      });

      return {
        criteriaId: criterion.id,
        criteriaTitle: criterion.title,
        weight: criterion.weight,
        mean,
        stdDev,
        memberScores,
      };
    });

    // Weighted score: Σ(weight × mean) / Σ(weight)
    const weightedScore =
      criteriaBreakdown.reduce((sum, cr) => sum + cr.weight * cr.mean, 0) / totalWeight;

    // Divergence: any criterion whose std-dev exceeds the threshold
    const isDivergent = criteriaBreakdown.some((cr) => cr.stdDev > divergenceThreshold);

    // Member participation per option
    const memberParticipation: GroupMemberParticipation[] = members.map((member) => {
      const memberCriteriaScored = criteria.filter((criterion) =>
        scores.some(
          (s) =>
            s.itemId === option.id &&
            s.criteriaId === criterion.id &&
            s.userId === member.userId,
        ),
      ).length;

      let status: GroupMemberParticipation['status'];
      if (memberCriteriaScored === 0) {
        status = 'not_started';
      } else if (memberCriteriaScored < criteria.length) {
        status = 'partial';
      } else {
        status = 'complete';
      }

      return {
        userId: member.userId,
        displayName: member.displayName,
        status,
        optionsScored: memberCriteriaScored,
      };
    });

    return {
      itemId: option.id,
      title: option.title,
      weightedScore,
      isDivergent,
      criteriaBreakdown,
      memberParticipation,
    };
  });

  // Sort descending by weightedScore, then assign rank (1 = winner).
  // Options with equal scores share the same rank.
  const sorted = [...optionResults].sort((a, b) => b.weightedScore - a.weightedScore);

  const rankedOptions: GroupOptionResult[] = sorted.map((opt, idx) => {
    // Determine rank: if this score equals the previous option's score, share its rank.
    const prevRank = idx > 0 ? rankedOptions[idx - 1].rank : 0;
    const prevScore = idx > 0 ? sorted[idx - 1].weightedScore : null;
    const rank = prevScore !== null && prevScore === opt.weightedScore ? prevRank : idx + 1;
    return { ...opt, rank };
  });

  return {
    sessionId: options.length > 0 ? scores[0]?.sessionId ?? '' : '',
    rankedOptions,
    divergenceThreshold,
    totalMembers,
    generatedAt: new Date().toISOString(),
  };
}
